import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { TimingTab } from '../contexts/OverviewContext';

dayjs.extend(weekOfYear);

export interface ChartLabelConfig {
  label1: string; // Current period
  label2: string; // Previous period  
  label3: string; // Two periods ago
}

/**
 * Calculates appropriate labels for line chart based on timing tab and start date
 * @param timingTab - The current timing tab (weekly/monthly/yearly)
 * @param startDate - The start date of the current period
 * @param t - Translation function from react-i18next
 * @returns Object with three labels for the chart lines
 */
export const calculateChartLabels = (
  timingTab: TimingTab,
  startDate: Date,
  t: (key: string, options?: any) => string
): ChartLabelConfig => {
  const now = new Date();
  const startDayjs = dayjs(startDate);
  const nowDayjs = dayjs(now);

  switch (timingTab) {
    case 'weekly': {
      const currentWeek = nowDayjs.week();
      const startWeek = startDayjs.week();
      const startYear = startDayjs.year();
      const currentYear = nowDayjs.year();

      // Check if we're in the current week of the current year
      if (startYear === currentYear && startWeek === currentWeek) {
        return {
          label1: t('time.thisWeek'),
          label2: t('time.lastWeek'),
          label3: t('time.twoWeeksAgo'),
        };
      } else {
        // Show specific week numbers
        const week1 = startWeek;
        const week2 = week1 > 1 ? week1 - 1 : 52; // Handle year boundary
        const week3 = week2 > 1 ? week2 - 1 : 51;

        return {
          label1: t('time.week', { week: week1 }),
          label2: t('time.week', { week: week2 }),
          label3: t('time.week', { week: week3 }),
        };
      }
    }

    case 'monthly': {
      return {
        label1: dayjs(startDate).format('M/YYYY'),
        label2: dayjs(startDate).subtract(1, 'month').format('M/YYYY'),
        label3: dayjs(startDate).subtract(2, 'month').format('M/YYYY'),
      };
    }

    case 'yearly': {
      const currentYear = nowDayjs.year();
      const startYear = startDayjs.year();

      // Check if we're in the current year
      if (startYear === currentYear) {
        return {
          label1: dayjs(startDate).format('YYYY'),
          label2: dayjs(startDate).subtract(1, 'year').format('YYYY'),
          label3: dayjs(startDate).subtract(2, 'year').format('YYYY'),
        };
      } else {
        return {
          label1: dayjs(startDate).format('YYYY'),
          label2: dayjs(startDate).subtract(1, 'year').format('YYYY'),
          label3: dayjs(startDate).subtract(2, 'year').format('YYYY'),
        };
      }
    }

    default:
      // Fallback to monthly logic
      return {
        label1: t('time.thisMonth'),
        label2: t('time.lastMonth'),
        label3: t('time.twoMonthsAgo'),
      };
  }
}; 